import { MinuteBasedCommercialProductType } from '../../components/OrderSubscriptionSelection/minuteBasedSubscriptionUtil.js';
import { CommercialProductSubType } from '../../generated/api/commercialProductSubType.js';
import { OnlineModelCategory } from '../../generated/api/onlineModelCategory.js';
import { ModelType } from '../enums.js';
import { eurosToCents } from './priceUtils.js';
import type { AddOn, CampaignAssociation, CommercialProduct, OnlineModel, Price } from '../../generated/api/models.js';
import type { AssociationRecord } from '../../legacy/cloudsense-add-on-dependency-engine/AddOnManagerOnline';
import type { ShoppingCartAddOn } from '../types/checkout.js';

export const SQUARETRADE = 'SQUARETRADE';

export const isSquareTradeAddOn = (addOn?: AddOn | ShoppingCartAddOn): boolean => addOn?.addOnType === SQUARETRADE;

/**
 * Get M2M opening fee (cents).
 * SALES_PRODUCT models have fee in CP oneTimeCharge.
 * SELF_SERVICE models have fee in avausmaksu addon. (can be deleted later)
 */
export const getM2MOpeningFee = (cpOneTimeCharge: number | undefined, onlineModel: OnlineModel): number => {
  // SALES_PRODUCT: fee is in CP oneTimeCharge
  if (onlineModel.category === OnlineModelCategory.SALES_PRODUCT && cpOneTimeCharge) {
    return cpOneTimeCharge;
  }

  // SELF_SERVICE: fee is in avausmaksu addon
  if (onlineModel.category === OnlineModelCategory.SELF_SERVICE) {
    const openingFeeAddon = onlineModel.addOns?.find(
      addon => addon.addOnProductName?.includes('avausmaksu') && addon.addOnOneTimeCharge
    );
    return openingFeeAddon?.addOnOneTimeCharge ?? 0;
  }

  return 0;
};

export const openingFeesAddonCodes = [
  '1c19a5bb-9a9d-30a3-95e1-81e1f2ce56f4',
  '921c3964-b760-2eab-bfc4-030f586ff9af',
  '921c3964-b760-2eab-bfc4-030f586ff9af',
  'ca0c9593-035f-4d04-89a0-58b897f11e62',
  '1f217d07-afd6-97ba-9be5-88957a241c0d',
  '5e5a6b8b-cf3a-5bf3-2fcd-1224e610eb6e',
  '16cbc1cc-3b29-f9b1-61aa-aa68fc69c4f5',
  '230930af-084f-761d-012b-c7b25f2e1ccd',
  '6577468d-ad5c-6ae5-a486-3e54466b15bd',
  'd088ce64-ac3a-2a12-c338-cc48b1b96d14',
  '7cc7b5bf-e990-aed8-2e5c-330d81a3b0e3',
  '0b3f0db4-2819-c912-8718-7e76f0aa63d1',
  '8fe53e0c-c867-2b25-2e9e-cdde4660083f', // Liittymän avausmaksu (generic subscription opening fee)
  '31d96d9c-0c86-0608-da54-be33c561d9f0', // Yritysliittymä 4G avausmaksu
  '015fe61e-6cd3-0e51-69a7-066a38ba6112', // Elisa Yritysdata -liittymän avausmaksu (MBB Yritysdata)
  'ec5da085-4942-b34e-2441-071189237c5e', // Elisa Yrityspuhe Avausmaksu
  '511bc4ce-8c18-ad13-06cf-0fe9808f4ea5', // M2M avausmaksu
  'fe31a18a-9934-0505-4df0-ace57c65d37a', // Avaus- / kertamaksu (MBB Liikkuva WiFi)
];

export const MBB_YRITYSDATA_OPENING_FEE_CODE = '015fe61e-6cd3-0e51-69a7-066a38ba6112';
export const MBB_LIIKKUVA_WIFI_OPENING_FEE_CODE = 'fe31a18a-9934-0505-4df0-ace57c65d37a';

/** Get MRC and OTC for a commercial product. Tries effectivePrice, then CP fields, then addons. */
export const getEffectiveCharges = (
  cp: CommercialProduct
): { monthlyRecurringCharge: number; oneTimeCharge: number } => {
  const fromEffectivePrice = cp.price?.effectivePrice;

  // MRC: effectivePrice → CP field → pricing addon (matched by CP name)
  const directMrc = fromEffectivePrice?.monthlyRecurringCharge || cp.monthlyRecurringCharge || 0;
  const monthlyRecurringCharge =
    directMrc > 0
      ? directMrc
      : (cp.associatedAddOns?.find(addon => addon.addOnProductName === cp.commercialProductName)?.price?.effectivePrice
          ?.monthlyRecurringCharge ?? 0);

  // OTC: effectivePrice → CP field → opening fee addon (by code, then name fallback)
  const directOtc = fromEffectivePrice?.oneTimeCharge || cp.oneTimeCharge || 0;
  if (directOtc > 0) {
    return { monthlyRecurringCharge, oneTimeCharge: directOtc };
  }
  const openingFeeCodeSet = new Set(openingFeesAddonCodes);
  const openingFeeAddon = cp.associatedAddOns?.find(
    addon =>
      openingFeeCodeSet.has(addon.addOnCode) ||
      addon.addOnProductName?.toLowerCase().includes('avausmaksu') ||
      addon.addOnProductName === 'Avaus- / kertamaksu'
  );
  const oneTimeCharge =
    openingFeeAddon?.price?.effectivePrice?.oneTimeCharge ?? openingFeeAddon?.addOnOneTimeCharge ?? 0;

  return { monthlyRecurringCharge, oneTimeCharge };
};

/** MBB SELF_SERVICE pricing: monthly from associatedAddOns, opening fee from onlineModel.addOns[]. */
export interface MobileBroadbandPricing {
  monthlyRecurringCharge: number;
  originalMonthlyRecurringCharge: number;
  oneTimeCharge: number;
  originalOneTimeCharge: number;
  isContractPrice: boolean;
  campaignAssociation?: CampaignAssociation;
}

export const getMobileBroadbandPricing = (
  commercialProduct: CommercialProduct,
  onlineModel: OnlineModel
): MobileBroadbandPricing => {
  const cpName = commercialProduct.commercialProductName;
  const pricingAddon = commercialProduct.associatedAddOns?.find(addon => addon.addOnProductName === cpName);

  const monthlyRecurringCharge = pricingAddon?.price?.effectivePrice?.monthlyRecurringCharge ?? 0;
  const originalMonthlyRecurringCharge = pricingAddon?.price?.listPrice?.monthlyRecurringCharge ?? 0;
  const campaignAssociation = pricingAddon?.price?.campaign?.campaignAssociation;
  const openingFeeAddon = onlineModel.addOns?.find(
    addon =>
      addon.addOnCode === MBB_YRITYSDATA_OPENING_FEE_CODE || addon.addOnCode === MBB_LIIKKUVA_WIFI_OPENING_FEE_CODE
  );

  const oneTimeCharge =
    openingFeeAddon?.price?.effectivePrice?.oneTimeCharge ?? openingFeeAddon?.addOnOneTimeCharge ?? 0;
  const originalOneTimeCharge =
    openingFeeAddon?.price?.listPrice?.oneTimeCharge ?? openingFeeAddon?.addOnOneTimeCharge ?? 0;

  const hasDiscountedPrice =
    monthlyRecurringCharge !== originalMonthlyRecurringCharge || oneTimeCharge !== originalOneTimeCharge;

  // When no pricing addon exists, fall back to CP prices
  if (!pricingAddon) {
    const cpMRC = commercialProduct.price?.effectivePrice?.monthlyRecurringCharge ?? 0;
    const cpOriginalMRC = commercialProduct.price?.listPrice?.monthlyRecurringCharge ?? 0;
    const cpOTC = commercialProduct.price?.effectivePrice?.oneTimeCharge ?? 0;
    const cpOriginalOTC = commercialProduct.price?.listPrice?.oneTimeCharge ?? 0;
    return {
      monthlyRecurringCharge: cpMRC,
      originalMonthlyRecurringCharge: cpOriginalMRC,
      oneTimeCharge: cpOTC,
      originalOneTimeCharge: cpOriginalOTC,
      isContractPrice: cpMRC !== cpOriginalMRC || cpOTC !== cpOriginalOTC,
    };
  }

  return {
    monthlyRecurringCharge,
    originalMonthlyRecurringCharge,
    oneTimeCharge,
    originalOneTimeCharge,
    // This will be true also when voucher is applied, but the voucher badge displaying is prioritized higher, so contract price badge won't show up in that case
    isContractPrice: hasDiscountedPrice,
    campaignAssociation,
  };
};

export const findActiveAddonAssociation = (
  associations: ReadonlyArray<AssociationRecord>,
  addonCode: string,
  groupName?: string
) =>
  associations.find(
    assoc =>
      assoc.cspmb__add_on_price_item__r.guid__c === addonCode &&
      assoc.is_active__c &&
      assoc.cspmb__add_on_price_item__r.cspmb__is_active__c &&
      (groupName ? assoc.cspmb__group__c === groupName : true)
  );

export const getAddonPrice = (association: Readonly<AssociationRecord>): Price => {
  return {
    oneTimeCharge: eurosToCents(association.cspmb__add_on_price_item__r.cspmb__one_off_charge__c),
    monthlyRecurringCharge: eurosToCents(association.cspmb__add_on_price_item__r.cspmb__recurring_charge__c),
  };
};

export const newAddon = (
  association: Readonly<AssociationRecord>,
  addOnGroup: string,
  addOnType = 'SERVICE'
): AddOn => {
  const addonPrice = getAddonPrice(association);

  return {
    addOnId: association.cspmb__add_on_price_item__c,
    addOnType,
    addOnGroup,
    addOnCode: association.cspmb__add_on_price_item__r.guid__c,
    addOnProductName: association.add_on_price_item_name__c,
    price: { listPrice: addonPrice, effectivePrice: addonPrice }, // AssociationRecord doesn't seem to hase separate effective & list prices, so using same price for both
    campaignAssociations: [],
  };
};

export const lookupAddonPrice = (associations: AssociationRecord[], addonCode: string) => {
  const association = findActiveAddonAssociation(associations, addonCode);
  if (association) {
    return getAddonPrice(association);
  }
  return undefined;
};

/** Voice SELF_SERVICE pricing from bundled addons. */
export interface VoiceSMEPricing {
  monthlyRecurringCharge: number;
  originalMonthlyRecurringCharge: number;
  oneTimeCharge: number;
  originalOneTimeCharge: number;
  isContractPrice: boolean;
  isCampaignPrice: boolean;
}

// Minute-based subscription commercial product GUIDs - these have separate CP base pricing
const MINUTE_BASED_CP_CODES: string[] = Object.values(MinuteBasedCommercialProductType);

export const getVoiceSMEPricing = (commercialProduct: CommercialProduct): VoiceSMEPricing => {
  // Bundled addons: isOfferAddOn=true or undefined (not explicitly excluded)
  const offerAddonCodes = commercialProduct.addOnAssociations
    ?.filter(a => a.isOfferAddOn !== false)
    .map(a => a.addOnCode);

  const bundledAddons =
    commercialProduct.associatedAddOns?.filter(addon => offerAddonCodes?.includes(addon.addOnCode)) ?? [];

  // Exclude OPTIONAL addons from monthly (separate purchasable services, not base price)
  const monthlyAddonCodes = commercialProduct.addOnAssociations
    ?.filter(a => a.isOfferAddOn !== false && a.addOnPurpose !== 'OPTIONAL')
    .map(a => a.addOnCode);
  const monthlyBundledAddons = bundledAddons.filter(addon => monthlyAddonCodes?.includes(addon.addOnCode));

  // Minute-based: include CP base pricing. Other Voice: addon-only pricing.
  const isMinuteBased = MINUTE_BASED_CP_CODES.includes(commercialProduct.commercialProductCode);
  const cpMonthlyCharge = isMinuteBased ? (commercialProduct.price?.effectivePrice?.monthlyRecurringCharge ?? 0) : 0;
  const cpOriginalMonthlyCharge = isMinuteBased ? (commercialProduct.price?.listPrice?.monthlyRecurringCharge ?? 0) : 0;
  const cpOneTimeCharge = isMinuteBased ? (commercialProduct.price?.effectivePrice?.oneTimeCharge ?? 0) : 0;
  const cpOriginalOneTimeCharge = isMinuteBased ? (commercialProduct.price?.listPrice?.oneTimeCharge ?? 0) : 0;

  const monthlyRecurringCharge =
    cpMonthlyCharge +
    monthlyBundledAddons.reduce((acc, addon) => acc + (addon.price?.effectivePrice?.monthlyRecurringCharge ?? 0), 0);
  const originalMonthlyRecurringCharge =
    cpOriginalMonthlyCharge +
    monthlyBundledAddons.reduce((acc, addon) => acc + (addon.price?.listPrice?.monthlyRecurringCharge ?? 0), 0);

  const oneTimeCharge =
    cpOneTimeCharge + bundledAddons.reduce((acc, addon) => acc + (addon.price?.effectivePrice?.oneTimeCharge ?? 0), 0);
  const originalOneTimeCharge =
    cpOriginalOneTimeCharge +
    bundledAddons.reduce((acc, addon) => acc + (addon.price?.listPrice?.oneTimeCharge ?? 0), 0);

  const addOnHasCampaign = monthlyBundledAddons.some(addon => addon.price?.campaign !== undefined);
  const cpHasCampaign = commercialProduct.price?.campaign !== undefined;

  // Use addon-calculated price when available, otherwise fall back to CP price that public models have.
  const finalMRC =
    monthlyRecurringCharge > 0
      ? monthlyRecurringCharge
      : (commercialProduct.price?.effectivePrice?.monthlyRecurringCharge ?? 0);
  const finalOriginalMRC =
    originalMonthlyRecurringCharge > 0
      ? originalMonthlyRecurringCharge
      : (commercialProduct.price?.listPrice?.monthlyRecurringCharge ?? 0);
  const finalOTC = oneTimeCharge > 0 ? oneTimeCharge : (commercialProduct.price?.effectivePrice?.oneTimeCharge ?? 0);
  const finalOriginalOTC =
    originalOneTimeCharge > 0 ? originalOneTimeCharge : (commercialProduct.price?.listPrice?.oneTimeCharge ?? 0);

  return {
    monthlyRecurringCharge: finalMRC,
    originalMonthlyRecurringCharge: finalOriginalMRC,
    oneTimeCharge: finalOTC,
    originalOneTimeCharge: finalOriginalOTC,
    isContractPrice: finalMRC !== finalOriginalMRC || finalOTC !== finalOriginalOTC,
    isCampaignPrice: cpHasCampaign || addOnHasCampaign,
  };
};

/** MBB detection by model code (Yritysdata + Liikkuva WiFi). */
export const isMobileBroadbandModel = (onlineModel: OnlineModel): boolean =>
  onlineModel.onlineModelCode === ModelType.MobileBroadband || onlineModel.onlineModelCode === ModelType.ElisaNetti;

/** M2M detection by productSubType or model code (SELF_SERVICE only). */
const isMobileM2MSelfService = (onlineModel: OnlineModel): boolean => {
  if (onlineModel.category !== OnlineModelCategory.SELF_SERVICE) {
    return false;
  }
  const hasM2MSubType =
    onlineModel.commercialProducts?.some(cp => cp.productSubType === CommercialProductSubType.M2M) === true;
  return hasM2MSubType || onlineModel.onlineModelCode === ModelType.MobileM2M;
};

/** Pricing result for SELF_SERVICE subscriptions (Voice and MBB). */
export interface SelfServicePricing {
  monthlyRecurringCharge: number;
  originalMonthlyRecurringCharge: number;
  oneTimeCharge: number;
  originalOneTimeCharge: number;
  isContractPrice?: boolean;
  campaignAssociation?: CampaignAssociation;
  isCampaignPrice?: boolean;
}

/**
 * Single entry point for SELF_SERVICE subscription pricing.
 * Returns undefined for non-SELF_SERVICE, M2M, and unrecognized product types.
 */
export const getSelfServiceSubscriptionPrice = (
  commercialProduct: CommercialProduct,
  onlineModel: OnlineModel
): SelfServicePricing | undefined => {
  if (onlineModel.category !== OnlineModelCategory.SELF_SERVICE) {
    return undefined;
  }

  // M2M uses variant addon pricing, not handled here
  if (isMobileM2MSelfService(onlineModel)) {
    return undefined;
  }

  // MBB (Yritysdata + Liikkuva WiFi)
  if (isMobileBroadbandModel(onlineModel)) {
    return getMobileBroadbandPricing(commercialProduct, onlineModel);
  }

  // Voice — explicit check by productSubType, no default fallback
  if (commercialProduct.productSubType === CommercialProductSubType.MOBILE_VOICE) {
    return getVoiceSMEPricing(commercialProduct);
  }

  return undefined;
};

export const getAddonsRecurringPrice = (commercialProduct: CommercialProduct, includeAddons?: boolean) => {
  return ((includeAddons && commercialProduct.associatedAddOns) || [])
    .map(addon => addon.price?.effectivePrice.monthlyRecurringCharge || 0)
    .reduce((acc, cur) => acc + cur, 0);
};
